import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
    Spinner,
    NoticeBox,
    NoticeBoxColors,
    NoticeBoxVariants,
    VsExclamationIcon,
    TextLink,
} from '@octane/spark'
import { getCollaterals } from 'api/application'
import {
    CollateralResponse,
    PowersportsAssetResponse,
} from 'api/application/getCollaterals'
import Card from 'components/Card'
import config from 'config'
import useClientApplication from 'hooks/useClientApplication'
import useLenderDisclosure from 'hooks/useLenderDisclosure'
import ApplicationStatus from 'state/enums/ApplicationStatus'
import DisclosureType from 'state/enums/DisclosureType'

import {
    MainContainer,
    LoaderWrapper,
    FinePrint,
    DisclosureContainer,
    DisclosureParagraph,
    DisclosureTitle,
    RuleLine,
    DisclosureSpinnerWrapper,
    FooterText,
} from './DecisionResult.styled'
import DecisionResultApproved from './DecisionResultApproved'
import DecisionResultCollateralCard from './DecisionResultCollateralCard'
import DecisionResultCustomerDetailCard from './DecisionResultCustomerDetailCard'
import DecisionResultDeclined from './DecisionResultDeclined'
import DecisionResultSubmitted from './DecisionResultSubmitted'

export default function DecisionResult(): JSX.Element {
    const { applicationId } = useParams()
    const [showDisclosure, setShowDisclosure] = useState(false)

    const { clientApplication, loading, error } =
        useClientApplication(applicationId)

    const [collaterals, setCollaterals] = useState<
        CollateralResponse<PowersportsAssetResponse>[]
    >([])

    const handleCollaterals = async () => {
        if (clientApplication?.uuid) {
            setCollaterals(await getCollaterals(clientApplication.uuid))
        }
    }

    useEffect(() => {
        handleCollaterals()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientApplication])

    const {
        disclosureText,
        error: disclosureError,
        loading: disclosureLoading,
    } = useLenderDisclosure(
        DisclosureType.CREDIT_SOFT_PULL_APPLICATION_APPROVAL,
        config.ROADRUNNER_LENDER_ID,
        clientApplication?.id,
    )

    const handleClickReadDisclosure = () => {
        setShowDisclosure(true)
    }

    const renderDisclosure = () => {
        if (!showDisclosure) {
            return null
        }

        if (disclosureError) {
            return (
                <DisclosureContainer>
                    <NoticeBox
                        // eslint-disable-next-line max-len
                        content="An error occurred when loading the disclosure. Refresh the page to try again or contact support."
                        heading=""
                        icon={VsExclamationIcon}
                        color={NoticeBoxColors.ERROR}
                        variant={NoticeBoxVariants.BOX_HIGHLIGHTED}
                    />
                </DisclosureContainer>
            )
        }

        if (disclosureLoading || !disclosureText) {
            return (
                <DisclosureSpinnerWrapper data-testid="DecisionResultApproved-Disclosure-spinner">
                    <Spinner size={24} />
                </DisclosureSpinnerWrapper>
            )
        }

        return (
            <DisclosureContainer>
                <RuleLine />
                <DisclosureTitle>Soft Credit Pull Disclosure</DisclosureTitle>
                <div>
                    {disclosureText.split('\n').map((paragraph) => (
                        <DisclosureParagraph key={paragraph}>
                            {paragraph}
                        </DisclosureParagraph>
                    ))}
                </div>
            </DisclosureContainer>
        )
    }

    if (error) {
        return (
            <MainContainer>
                <NoticeBox
                    // eslint-disable-next-line max-len
                    content="An error occurred when loading the decision. Refresh the page to try again or contact support."
                    heading=""
                    icon={VsExclamationIcon}
                    color={NoticeBoxColors.ERROR}
                    variant={NoticeBoxVariants.BOX_HIGHLIGHTED}
                />
            </MainContainer>
        )
    }

    if (loading || !clientApplication || !collaterals) {
        return (
            <LoaderWrapper data-testid="DecisionResult-spinner">
                <Spinner />
            </LoaderWrapper>
        )
    }

    const renderContent = () => {
        switch (clientApplication?.status) {
            case ApplicationStatus.APPROVED:
                return (
                    <DecisionResultApproved
                        clientApplication={clientApplication}
                        vehicleType={collaterals[0]?.asset.vehicle_type}
                        vehicleCondition={collaterals[0]?.asset.condition}
                    />
                )

            case ApplicationStatus.DECLINE:
                return (
                    <DecisionResultDeclined
                        clientApplication={clientApplication}
                    />
                )

            default:
                return (
                    <DecisionResultSubmitted
                        clientApplication={clientApplication}
                    />
                )
        }
    }

    return (
        <MainContainer>
            <Card style={{ padding: '20px' }}>{renderContent()}</Card>
            <DecisionResultCustomerDetailCard
                clientApplication={clientApplication}
            />
            <DecisionResultCollateralCard
                applicationId={clientApplication.id}
                collaterals={collaterals}
            />
            <FinePrint>
                <FooterText>
                    Applicants are subject to a full credit approval.{' '}
                </FooterText>
                <TextLink
                    onClick={handleClickReadDisclosure}
                    data-testid="DecisionResultApproved-read-disclosure-link"
                >
                    Read Disclosure
                </TextLink>
            </FinePrint>
            <div>{renderDisclosure()}</div>
        </MainContainer>
    )
}
